import React from 'react'
import { Box, styled, Tooltip, Typography } from '@mui/material'
import { ReportComponentType } from 'src/types/report-designer'

export interface IComponentBlockProps {
  type: ReportComponentType
  title: string
  description: string
  icon: React.ReactNode
  accentColor: string
  disabled?: boolean
  disabledTooltip?: string
}

export const ComponentBlock: React.FC<IComponentBlockProps> = ({
  type,
  title,
  description,
  icon,
  accentColor,
  disabled = false,
  disabledTooltip,
}) => {
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData('componentType', String(type))
    e.dataTransfer.effectAllowed = 'copy'
  }

  const card = (
    <BlockCard draggable={!disabled} onDragStart={handleDragStart} accentColor={accentColor} isDisabled={disabled}>
      <IconWrapper accentColor={accentColor} isDisabled={disabled}>
        {icon}
      </IconWrapper>
      <BlockText>
        <Typography variant="body1" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" fontWeight={400}>
          {description}
        </Typography>
      </BlockText>
    </BlockCard>
  )

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} placement="right" arrow>
        <span>{card}</span>
      </Tooltip>
    )
  }

  return card
}

interface IAccentProps {
  accentColor: string
  isDisabled?: boolean
}

const BlockCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'accentColor' && prop !== 'isDisabled',
})<IAccentProps>(({ theme, accentColor, isDisabled }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${isDisabled ? theme.palette.divider : `${accentColor}40`}`,
  backgroundColor: isDisabled ? theme.palette.action.disabledBackground : `${accentColor}12`,
  cursor: isDisabled ? 'not-allowed' : 'grab',
  userSelect: 'none',
  opacity: isDisabled ? 0.5 : 1,
  transition: 'box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
  '&:hover': isDisabled
    ? {}
    : {
        boxShadow: `0 2px 8px ${accentColor}30`,
        borderColor: accentColor,
        backgroundColor: `${accentColor}1e`,
      },
  '&:active': isDisabled ? {} : { cursor: 'grabbing' },
}))

const IconWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'accentColor' && prop !== 'isDisabled',
})<IAccentProps>(({ accentColor, isDisabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 6,
  backgroundColor: isDisabled ? 'rgba(0,0,0,0.08)' : `${accentColor}33`,
  color: isDisabled ? 'rgba(0,0,0,0.38)' : accentColor,
  flexShrink: 0,
  '& svg': {
    fontSize: '1.1rem',
  },
}))

const BlockText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
})
